import { color as d3Color, rgb as d3Rgb } from "d3-color";
import { useMemo } from "react";

import { useTheme } from "@/context/ThemeContext";

/**
 * Resolved chart colors, read from the `--chart-*` CSS custom properties so
 * charts follow the app's light/dark tokens without a separate theme config.
 */
export interface ChartTheme {
  /** Categorical series palette (cycled when there are more series than colors). */
  series: string[];
  /** Savings / gains. */
  positive: string;
  /** Cost increases / losses. */
  negative: string;
  /** Gridline color (usually low-alpha). */
  grid: string;
  /** Axis tick + label color. */
  axis: string;
  /** Heatmap cell value at low magnitude. */
  heatmapValueQuiet: string;
  /** Heatmap cell value at peak magnitude (on pale cells). */
  heatmapValueLoud: string;
  /** Heatmap cell value on saturated / hot fill (light mode). */
  heatmapValueOnHot: string;
  /** Heatmap cell background at zero / low magnitude (opaque). */
  heatmapFillMin: string;
  /** Heatmap cell background at peak magnitude (opaque). */
  heatmapFillMax: string;
}

const FALLBACK: ChartTheme = {
  series: ["#2f7fd1", "#3fb9c9", "#d9a441", "#d95c4a", "#8b5cf6", "#4fb477"],
  positive: "#4fb477",
  negative: "#d95c4a",
  grid: "rgba(15,23,42,0.1)",
  axis: "#6b7280",
  heatmapValueQuiet: "#9ca3af",
  heatmapValueLoud: "#0f172a",
  heatmapValueOnHot: "#ffffff",
  heatmapFillMin: "#eef0fa",
  heatmapFillMax: "#6f5cc9",
};

/** Pick light or dark label text for a heatmap cell fill (WCAG relative luminance). */
export function heatmapTextOnFill(fillRgb: string): string {
  const parsed = d3Color(fillRgb);
  if (!parsed) return "rgb(30, 27, 45)";
  const c = d3Rgb(parsed);
  const channel = (v: number) => {
    const x = v / 255;
    return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4;
  };
  const luminance =
    0.2126 * channel(c.r) + 0.7152 * channel(c.g) + 0.0722 * channel(c.b);
  return luminance > 0.5 ? "rgb(30, 27, 45)" : "rgb(248, 250, 252)";
}

/**
 * Resolve a theme token to `rgb(...)` for D3 scales. D3 cannot parse oklch/hsl
 * strings; hex and browser-resolved rgb are safe.
 */
export function colorForD3Interpolation(input: string, fallback: string): string {
  const trimmed = input.trim();
  const parsed = d3Color(trimmed);
  if (parsed && parsed.opacity > 0) {
    return parsed.formatRgb();
  }
  if (typeof document !== "undefined") {
    const probe = document.createElement("div");
    probe.style.backgroundColor = trimmed;
    probe.style.display = "none";
    document.body.appendChild(probe);
    const resolved = getComputedStyle(probe).backgroundColor;
    probe.remove();
    const fromDom = d3Color(resolved);
    if (fromDom && fromDom.opacity > 0) {
      return fromDom.formatRgb();
    }
  }
  const fb = d3Color(fallback);
  return fb ? fb.formatRgb() : fallback;
}

/** @deprecated Use {@link colorForD3Interpolation} */
export function cssColorToRgb(color: string): string {
  return colorForD3Interpolation(color, color);
}

function readVar(styles: CSSStyleDeclaration, name: string, fallback: string): string {
  const value = styles.getPropertyValue(name).trim();
  return value || fallback;
}

function readChartTheme(): ChartTheme {
  if (typeof window === "undefined") return FALLBACK;
  const styles = getComputedStyle(document.documentElement);
  return {
    series: [
      readVar(styles, "--chart-1", FALLBACK.series[0]),
      readVar(styles, "--chart-2", FALLBACK.series[1]),
      readVar(styles, "--chart-3", FALLBACK.series[2]),
      readVar(styles, "--chart-4", FALLBACK.series[3]),
      readVar(styles, "--chart-5", FALLBACK.series[4]),
      readVar(styles, "--chart-6", FALLBACK.series[5]),
    ],
    positive: readVar(styles, "--chart-positive", FALLBACK.positive),
    negative: readVar(styles, "--chart-negative", FALLBACK.negative),
    grid: readVar(styles, "--chart-grid", FALLBACK.grid),
    axis: readVar(styles, "--chart-axis", FALLBACK.axis),
    heatmapValueQuiet: readVar(
      styles,
      "--chart-heatmap-value-quiet",
      FALLBACK.heatmapValueQuiet,
    ),
    heatmapValueLoud: readVar(
      styles,
      "--chart-heatmap-value-loud",
      FALLBACK.heatmapValueLoud,
    ),
    heatmapValueOnHot: readVar(
      styles,
      "--chart-heatmap-value-on-hot",
      FALLBACK.heatmapValueOnHot,
    ),
    heatmapFillMin: readVar(
      styles,
      "--chart-heatmap-fill-min",
      FALLBACK.heatmapFillMin,
    ),
    heatmapFillMax: readVar(
      styles,
      "--chart-heatmap-fill-max",
      FALLBACK.heatmapFillMax,
    ),
  };
}

/**
 * Read the chart palette from CSS variables, re-reading whenever the resolved
 * theme flips so a light/dark toggle recolors live charts.
 */
export function useChartTheme(): ChartTheme {
  const { theme } = useTheme();
  return useMemo(() => readChartTheme(), [theme]);
}

/** Pick a series color by index, cycling through the palette. */
export function seriesColor(palette: string[], index: number): string {
  return palette[index % palette.length];
}
